import { saveAs } from 'file-saver';
import { ReactElement } from 'react';

// Import PDF components
import LoanEligibilityGuide from '@/components/resources/LoanEligibilityGuide';
import AdvancedEMICalculatorExcel from '@/components/resources/AdvancedEMICalculatorExcel';
import DocumentationChecklist from '@/components/resources/DocumentationChecklist';
import InterestRateComparison from '@/components/resources/InterestRateComparison';
import FinancialPlanningHandbook from '@/components/resources/FinancialPlanningHandbook';
import CreditScoreGuide from '@/components/resources/CreditScoreGuide';

type ComponentType = () => ReactElement<any, any>;

type ResourceType = 
  | 'eligibility-guide'
  | 'emi-calculator'
  | 'documentation-checklist'
  | 'interest-rates'
  | 'financial-planning'
  | 'credit-score-guide';

const getPdfComponent = (type: ResourceType): ComponentType => {
  const components: Record<ResourceType, ComponentType> = {
    'eligibility-guide': LoanEligibilityGuide,
    'emi-calculator': AdvancedEMICalculatorExcel,
    'documentation-checklist': DocumentationChecklist,
    'interest-rates': InterestRateComparison,
    'financial-planning': FinancialPlanningHandbook,
    'credit-score-guide': CreditScoreGuide
  };
  
  return components[type] || LoanEligibilityGuide;
};

// Alias for backward compatibility
export const generateLoanEligibilityGuide = async (): Promise<Blob> => {
  return generatePdfBlob('eligibility-guide');
};

export const generatePdfBlob = async (type: ResourceType): Promise<Blob> => {
  try {
    const { pdf } = await import('@react-pdf/renderer');
    const PdfComponent = getPdfComponent(type);
    const element = PdfComponent();
    const blob = await pdf(element).toBlob();
    return blob;
  } catch (error) {
    console.error('Error generating PDF blob:', error);
    throw new Error('Failed to generate PDF');
  }
};

export const downloadPdf = async (blob: Blob, fileName: string): Promise<void> => {
  try {
    saveAs(blob, fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw new Error('Failed to download PDF');
  }
};

export const openPdfInNewTab = async (blob: Blob): Promise<void> => {
  try {
    const pdfUrl = URL.createObjectURL(blob);
    window.open(pdfUrl, '_blank');
  } catch (error) {
    console.error('Error opening PDF in new tab:', error);
    throw error;
  }
};

// Helper function to open a blob in a new tab
export const openBlobInNewTab = (blob: Blob) => {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Don't revoke the URL immediately to allow the browser to load it
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};
